import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DateField } from "@/components/DateField";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useHolidayChecker } from "@/lib/holidays";
import { fmtDate } from "@/lib/status";
import { Loader2, Lock, CalendarClock } from "lucide-react";

const blank = { name: "", category_id: "", reference: "", issuer: "", notes: "", due_date: "", threshold_days: "" };

export const ItemFormDialog = ({ open, onOpenChange, categories, item = null, onSaved }) => {
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);
  const editing = Boolean(item);
  const { user } = useAuth();
  const { ensureYear, check } = useHolidayChecker(user?.org?.holiday_country);

  // Load holiday data for the chosen year (and the previous one, in case the
  // "earlier working day" suggestion walks across a year boundary).
  useEffect(() => {
    if (!form.due_date) return;
    const year = new Date(form.due_date + "T00:00:00").getFullYear();
    if (!Number.isNaN(year)) {
      ensureYear(year);
      ensureYear(year - 1);
    }
  }, [form.due_date, ensureYear]);

  const dateWarning = form.due_date ? check(form.due_date) : null;

  useEffect(() => {
    if (open) {
      setForm(item ? {
        name: item.name || "",
        category_id: item.category_id || "",
        reference: item.reference || "",
        issuer: item.issuer || "",
        notes: item.notes || "",
        due_date: item.due_date || "",
        threshold_days: item.threshold_days ?? "",
      } : blank);
    }
  }, [open, item]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.category_id) {
      toast.error("Pick a category for this item.");
      return;
    }
    if (!form.due_date) {
      toast.error("Pick the due date — it's what reminders are based on.");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        category_id: form.category_id,
        reference: form.reference.trim() || null,
        issuer: form.issuer.trim(),
        notes: form.notes.trim(),
        due_date: form.due_date,
        threshold_days: form.threshold_days === "" ? null : Number(form.threshold_days),
      };
      let saved;
      if (editing) {
        if (payload.threshold_days === null) payload.clear_threshold = true;
        if (payload.reference === null) payload.clear_reference = true;
        saved = (await api.patch(`/items/${item.id}`, payload)).data;
        toast.success("Item updated.");
      } else {
        saved = (await api.post("/items", payload)).data;
        toast.success("Item added. We'll remind you before it's due.");
      }
      onOpenChange(false);
      onSaved?.(saved);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit item" : "Add an item"}</DialogTitle>
          <DialogDescription>
            {editing ? "Update the details below." : "Anything with an expiry date — a permit, policy, license, or registration."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="item-name">Name</Label>
            <Input id="item-name" required maxLength={200} value={form.name}
              onChange={(e) => set("name", e.target.value)}
              placeholder="e.g. Fleet van insurance — VAN-042" data-testid="item-form-name-input" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Category</Label>
              <Select value={form.category_id} onValueChange={(v) => set("category_id", v)}>
                <SelectTrigger data-testid="item-form-category-select">
                  <SelectValue placeholder="Pick a category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((c) => (
                    <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Due date</Label>
              <DateField value={form.due_date} onChange={(v) => set("due_date", v)} testId="item-form-due-date" />
            </div>
          </div>
          {dateWarning && (
            <div className="rounded-xl bg-[hsl(var(--status-due-soon)/0.10)] ring-1 ring-[hsl(var(--status-due-soon)/0.20)] px-3.5 py-3 flex items-start gap-2.5"
              data-testid="item-form-holiday-warning">
              <CalendarClock className="h-4 w-4 mt-0.5 shrink-0 text-[hsl(var(--status-due-soon))]" />
              <div className="text-sm">
                <p className="text-[hsl(var(--status-due-soon))] font-medium">
                  {dateWarning.type === "holiday"
                    ? `That due date is a public holiday (${dateWarning.label}).`
                    : `That due date falls on ${dateWarning.label}.`}
                </p>
                <p className="text-muted-foreground mt-0.5 leading-relaxed">
                  Offices may be closed, so renewing on the day could run late.
                  {dateWarning.suggestion
                    ? " We suggest moving it earlier to the nearest working day."
                    : " Consider setting it to an earlier working day."}
                </p>
                {dateWarning.suggestion && (
                  <Button type="button" size="sm" variant="secondary" className="rounded-lg mt-2"
                    onClick={() => set("due_date", dateWarning.suggestion)}
                    data-testid="item-form-holiday-suggestion-button">
                    Use {fmtDate(dateWarning.suggestion)}
                  </Button>
                )}
              </div>
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="item-issuer">Issuing organization or authority</Label>
            <Input id="item-issuer" maxLength={200} value={form.issuer}
              onChange={(e) => set("issuer", e.target.value)}
              placeholder="e.g. Department of Transport" data-testid="item-form-issuer-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="item-reference" className="flex items-center gap-1.5">
              Reference number <span className="text-muted-foreground font-normal">(optional)</span>
            </Label>
            <Input id="item-reference" maxLength={200} value={form.reference}
              onChange={(e) => set("reference", e.target.value)}
              placeholder="e.g. policy or permit number" className="font-mono-ref" data-testid="item-form-reference-input" />
            <p className="text-xs text-muted-foreground flex items-center gap-1">
              <Lock className="h-3 w-3" /> Encrypted at rest — only visible to your organization.
            </p>
          </div>
          <div className="space-y-2">
            <Label htmlFor="item-threshold">
              Remind me <span className="text-muted-foreground font-normal">(days before due date, optional)</span>
            </Label>
            <Input id="item-threshold" type="number" min={0} max={365} value={form.threshold_days}
              onChange={(e) => set("threshold_days", e.target.value)}
              placeholder="Leave blank to use the category or organization default"
              data-testid="item-form-threshold-input" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="item-notes">Notes <span className="text-muted-foreground font-normal">(optional)</span></Label>
            <Textarea id="item-notes" maxLength={2000} rows={3} value={form.notes}
              onChange={(e) => set("notes", e.target.value)}
              placeholder="Anything your team should know about this renewal" data-testid="item-form-notes-input" />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} className="rounded-xl">
              Cancel
            </Button>
            <Button type="submit" disabled={busy} className="rounded-xl" data-testid="item-form-submit-button">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : editing ? "Save changes" : "Add item"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
